<template>
	<div>
		<!-- Banner Image -->
		<img
			class="banner-image"
			src="../assets/images/bts-membership-banner.jpg"
		/>

		<!-- Application Form -->
		<!-- TODO: Add - errors to scope slot and iterate error in errors near submit button to guide the user -->
		<ValidationObserver ref="observer" v-slot="{ invalid }">
			<section class="section">
				<div class="container">
					<b-steps>
						<!-- Membership Levels Step -->
						<b-step-item label="Membership Levels" icon="account-cog">
							<b-message type="is-primary" title="Please Note:" closable>
								<p>
									Members of the following affiliated organisations can specify this option at the next stage and receive a 50% discount on the pricing shown below:
									<ul>
										<li><strong>ALTP</strong></li>
										<li><strong>BAPN</strong></li>
										<li><strong>BARTA</strong></li>
										<li><strong>BASL/BLTG</strong></li>
										<li><strong>BSHI</strong></li>
										<li><strong>BUTS</strong></li>
										<li><strong>SOTPA</strong></li>
									</ul>
								</p>
								<br />
								<p>
									We offer joint BTS membership options with the following organisations:
									<ul>
										<li><strong>The Herrick Society</strong></li>
										<li><strong>BTPT</strong></li>
									</ul>
								</p>
								<br />
								<p>Members looking for <strong>The Herrick Society Only</strong> or <strong>BTPT Only</strong> membership can specify this at the next stage.</p>
								<br />
								<p>Please select your chosen 'Member Level' and use the arrows at the bottom of the page to continue.</p>
							</b-message>

							<!-- Membership Types -->
							<!-- First Row: Original 3 membership types -->
							<div class="columns is-multiline has-text-centered">
								<div
									class="column is-4"
									v-for="(memberType, index) in availableMembershipTypes.slice(0, 3)"
									:key="index"
								>
									<MemberTypeCard
										:img="`${memberType.imageName}-membership.png`"
										:selected="applicationData.fields.selectedMemberType === memberType.type"
									>
										<template v-slot:content>
											<p>
												<b>NHS Medical:</b>
												{{ memberType.nhsMedical }}
											</p>
											<p>
												<b>Non-Clinical Scientist:</b>
												{{ memberType.nonClinical }}
											</p>
											<p>
												<b>A4C:</b>
												{{ memberType.nhs }}
											</p>
											<p class="has-text-primary">
												<b>{{ memberType.price }}</b>
												<i> per Year.</i>
											</p>
											<p>Membership expires annually on 31st October.</p>
										</template>
										<template v-slot:footer="{ selected }">
											<a
												:class="['card-footer-item', 'ksam-card-footer-item', { selected }]"
												@click="applyMemberCode(memberType)"
											>
												<b>{{ selected ? 'SELECTED' : 'SELECT' }}</b>
											</a>
										</template>
									</MemberTypeCard>
								</div>
							</div>
						</b-step-item>

						<!-- Personal Information Step -->
						<b-step-item label="Personal Information" icon="account">
							<div class="columns is-multiline">
								<div class="column is-12">
									<BSelectWithValidation
										rules="required"
										label="Title"
										icon="format-title"
										placeholder="Please Select a Title"
										v-model="applicationData.fields.title"
									>
										<option
											v-for="(title, index) in titles"
											:key="index"
											:value="title"
										>
											{{ title }}
										</option>
									</BSelectWithValidation>
								</div>
								<div class="column is-6">
									<BInputWithValidation
										rules="required"
										label="First Name"
										placeholder="First Name"
										icon="account"
										v-model="applicationData.fields.firstName"
									/>
								</div>
								<div class="column is-6">
									<BInputWithValidation
										rules="required"
										label="Last Name"
										placeholder="Last Name"
										icon="account"
										v-model="applicationData.fields.lastName"
									/>
								</div>
								<div class="column is-6">
									<BInputWithValidation
										rules="required"
										label="Job Title"
										placeholder="Job Title"
										icon="account-group"
										v-model="applicationData.fields.jobTitle"
									/>
								</div>
								<div class="column is-6">
									<BInputWithValidation
										rules="required"
										label="Company (e.g. NHS Trust or University)"
										placeholder="Company"
										icon="account-group"
										v-model="applicationData.fields.company"
									/>
								</div>

								<div class="column is-6">
									<BInputWithValidation
										rules="required|numeric"
										label="Phone Number"
										placeholder="Phone Number"
										icon="phone"
										v-model="applicationData.fields.phoneNumber"
									/>
								</div>
								<div class="column is-6">
									<BInputWithValidation
										rules="required|email"
										label="Email Address"
										placeholder="example@example.com"
										icon="email"
										v-model="applicationData.fields.email"
									/>
								</div>

								<!-- UserName Notification -->
								<div class="column is-12">
									<b-message>
										<b>Please Note:</b> your Email Address is used as your
										'Username', when first logging in please select 'Forgotten
										Password' to finish setting up your account
									</b-message>
								</div>

								<!-- 
								<div class="column is-6">
									<BSelectWithValidation
										rules="required"
										label="Field of Work"
										icon="briefcase"
										placeholder="Please Select a Field"
										expanded
										multiple
										v-model="applicationData.fields.fieldsOfWork"
									>
										<option
											v-for="(field, index) in fieldsOfWork"
											:key="index"
											:value="field.code"
										>
											{{ field.desc }}
										</option>
									</BSelectWithValidation>
									<div>
										<p class="has-text-primary is-italic">
											You may select multiple options by 'Control-Click'
											(Windows) or 'Command-Click' (Mac)
										</p>
										<p></p>
									</div>
								</div>
								-->

								<div class="column is-6">
									<BSelectWithValidation
										label="Affiliated Organisation"
										icon="briefcase"
										placeholder="Affiliated Organisation"
										expanded
										v-model="applicationData.fields.affiliatedOrganization"
									>
										<option :value="null">None</option>
										<option
											v-for="(org, index) in affiliatedOrganizations"
											:key="index"
											:value="org.code"
										>
											{{ org.desc }}
										</option>
									</BSelectWithValidation>
									<span class="is-size-7 has-text-grey">
										Members of affiliated organisations are entitled to a 50%
										reduction in fees.
									</span>
								</div>

								<div class="column is-12 has-text-centered">
									<h1 class="title">
										Address Details
									</h1>
								</div>

								<!-- Primary Address -->
								<div class="columns is-multiline">
									<div class="column is-6">
										<BInputWithValidation
											rules="required"
												label="Address 1"
											placeholder="Address 1"
											icon="map-marker"
											v-model="applicationData.fields.primaryAddress.address1"
										/>
									</div>
									<div class="column is-6">
											<BInputWithValidation
												label="Address 2"
												placeholder="Address 2"
												icon="map-marker"
												v-model="applicationData.fields.primaryAddress.address2"
											/>
									</div>

									<div class="column is-6">
											<BInputWithValidation
												label="Address 3"
												placeholder="Address 3"
												icon="map-marker"
												v-model="applicationData.fields.primaryAddress.address3"
											/>
									</div>
									<div class="column is-6">
										<BInputWithValidation
											rules="required"
												label="City"
											placeholder="City"
											icon="map-marker"
											v-model="applicationData.fields.primaryAddress.city"
										/>
									</div>

									<div class="column is-6">
										<BSelectWithValidation
											rules="required"
												label="County"
											icon="map-marker"
											placeholder="Please Select a County"
											v-model="applicationData.fields.primaryAddress.county"
											expanded
										>
											<option
												v-for="(county, index) in counties"
												:key="index"
												:value="county.code"
											>
												{{ county.desc }}
											</option>
										</BSelectWithValidation>
									</div>
									<div class="column is-6">
										<BInputWithValidation
											rules="required|max:10"
											label="Post Code"
											placeholder="Post Code"
											icon="map-marker"
											maxlength="10"
											v-model="applicationData.fields.primaryAddress.postCode"
										/>
									</div>
									<div class="column is-6">
										<BSelectWithValidation
											rules="required"
												label="Country"
											icon="map-marker"
											placeholder="Please Select a Country"
											v-model="applicationData.fields.primaryAddress.country"
											expanded
										>
											<option
												v-for="(country, index) in countries"
												:key="index"
												:value="country.code"
											>
												{{ country.desc }}
											</option>
										</BSelectWithValidation>
									</div>
									<div class="column is-6"></div>
								</div>

							</div>
						</b-step-item>

						<!-- EDI Details Step -->
						<b-step-item label="EDI Details" icon="account">
							<div class="columns is-multiline">
								<div class="column is-6">
									<BSelectWithValidation
										rules="required"
										label="What is your role in Transplantation?"
										icon="briefcase"
										placeholder="Please Select"
										expanded
										v-model="applicationData.fields.ediRole"
									>
										<option
											v-for="(ediRole, index) in ediRoles"
											:key="index"
											:value="ediRole.code"
										>
											{{ ediRole.desc }}
										</option>
									</BSelectWithValidation>
									<BInputWithValidation
										v-if="applicationData.fields.ediRole === 'OTH'"
										v-model="applicationData.fields.ediRoleOther"
										label="Please specify your role"
										placeholder="Enter your role (max 200 characters)"
										:rules="roleOtherRules"
										maxlength="200"
									/>
								</div>
								<div class="column is-6">
									<BCheckboxGroupWithValidation
										rules="required"
										label="Which organ transplant are you most associated with?"
										v-model="applicationData.fields.ediOrgan"
										:options="ediOrgans.map(o => ({ value: o.code, label: o.desc }))"
										columns="2"
									/>
									<BInputWithValidation
										v-if="(applicationData.fields.ediOrgan || []).includes('OTH')"
										v-model="applicationData.fields.ediOrganOther"
										label="Please specify which organ"
										placeholder="Enter organ (max 200 characters)"
										:rules="organOtherRules"
										maxlength="200"
									/>
								</div>
								<div class="column is-6">
									<BRadioGroupWithValidation
										label="Please confirm your age"
										v-model="applicationData.fields.ediAge"
										:options="ediAges.map(a => ({ value: a.code, label: a.desc }))"
										columns="2"
									/>
								</div>
								<div class="column is-6">
									<BSelectWithValidation
										label="Please confirm your sex at birth?"
										icon="account"
										placeholder="Please Select"
										expanded
										v-model="applicationData.fields.ediSex"
									>
										<option
											v-for="(ediSex, index) in ediSexes"
											:key="index"
											:value="ediSex.code"
										>
											{{ ediSex.desc }}
										</option>
									</BSelectWithValidation>
								</div>
							</div>
							<div class="columns is-multiline">
								<div class="column is-6">
									<BSelectWithValidation
										label="What is your Ethnicity?"
										icon="account"
										placeholder="Please Select"
										expanded
										v-model="applicationData.fields.ediEthnicity"
									>
										<option
											v-for="(ediEthnicity, index) in ediEthnicities"
											:key="index"
											:value="ediEthnicity.code"
										>
											{{ ediEthnicity.desc }}
										</option>
									</BSelectWithValidation>
								</div>
								<div class="column is-6">
									<BSelectWithValidation
										label="Do you consider yourself to have a Disability or Health Condition?"
										icon="account"
										placeholder="Please Select"
										expanded
										v-model="applicationData.fields.ediDisability"
									>
										<option
											v-for="(ediDisability, index) in ediDisabilities"
											:key="index"
											:value="ediDisability.code"
										>
											{{ ediDisability.desc }}
										</option>
									</BSelectWithValidation>
									<BInputWithValidation
										v-if="applicationData.fields.ediDisability === 'OTH'"
										v-model="applicationData.fields.ediDisabilityOther"
										label="Please specify"
										placeholder="Enter details (max 200 characters)"
										:rules="disabilityOtherRules"
										maxlength="200"
									/>
								</div>
							</div>
							<!--
							<div class="columns is-multiline">
								<div class="column is-6">
									<BSelectWithValidation
										label="What is your sexual orientation?"
										icon="account"
										placeholder="Please Select"
										expanded
										v-model="applicationData.fields.ediOrientation"
									>
										<option
											v-for="(ediOrientation, index) in ediOrientations"
											:key="index"
											:value="ediOrientation.code"
										>
											{{ ediOrientation.desc }}
										</option>
									</BSelectWithValidation>
								</div>
							</div>
							-->
						</b-step-item>
						
						<!-- Supporter Information Step -->
						<b-step-item
							label="Supporter Information"
							icon="account-supervisor"
						>
							<div class="columns">
								<div class="column is-6">
									<BInputWithValidation
										rules="required"
										label="First Supporter BTS Membership Number"
										placeholder="First Supporter BTS Membership Number"
										icon="numeric-1"
										v-model="applicationData.fields.firstSupporter"
									/>
								</div>
								<div class="column is-6">
									<BInputWithValidation
										rules="required"
										label="Second Supporter BTS Membership Number"
										placeholder="Second Supporter BTS Membership Number"
										icon="numeric-2"
										v-model="applicationData.fields.secondSupporter"
									/>
								</div>
							</div>
							<div class="columns">
								<div class="column is-12">
									<b-notification
										type="is-info"
										aria-close-label="Close notification"
									>
										<p>
											If you're unsure of your 'Supporters' Membership Number,
											please ask them.
										</p>
										<p>
											Alternatively, you can get in touch with us at
											<a href="mailto:secratariat@bts.org.uk"
												>secretariat@bts.org.uk</a
											>
											and we will help you locate these.
										</p>
									</b-notification>
								</div>
							</div>
						</b-step-item>

						<!-- Terms and Conditions Step -->
						<b-step-item label="Society Rules &amp; Code of Conduct" icon="file-question">
							<div class="columns is-multiline">
								<div class="column is-12">
									<TermsAndConditions :terms-accepted="termsAccepted" />
								</div>
								
								<div class="column is-12">
									<div class="buttons">
										<b-button
											type="is-primary"
											:disabled="termsAccepted"
											@click="acceptTerms"
											>Accept Society Rules & Code of Conduct</b-button
										>
										<b-button
											type="is-primary"
											@click="handleSubmit"
											:loading="isSubmitting"
											:disabled="
												invalid ||
													!termsAccepted ||
													!applicationData.fields.memberCode ||
													isSubmitting
											"
										>
											{{ isSubmitting ? 'Submitting...' : 'Submit Application' }}
										</b-button>
									</div>
									<div class="column is-12">
										<span class="is-size-7 has-text-grey">
											Please ensure you have 'Accepted' the Society Rules &amp;
											Code of Conduct and have completed all required fields with no
											errors prior to submitting.
										</span>
									</div>
								</div>
							</div>
						</b-step-item>
						
						<!-- Selected MemberType Notification -->
						<div style="margin-top: 2rem;">
							<SelectedMemberNotification
								:selectedMemberType="applicationData.fields.selectedMemberType"
								:affiliatedOrganization="applicationData.fields.affiliatedOrganization"
							/>
						</div>
					</b-steps>
				</div>
			</section>
		</ValidationObserver>
	</div>
</template>

<script>
import MemberTypeCard from '../components/MemberTypeCard';
import SelectedMemberNotification from '../components/SelectedMemberNotification';
import TermsAndConditions from '../components/TermsAndConditions';

// VeeValidate
import { ValidationObserver } from 'vee-validate';

// Axios
import axios from 'axios';
import qs from 'qs';

// Inputs with PreBuilt Validation Provider from VeeValidate
import BInputWithValidation from '../components/inputs/BInputWithValidation';
import BSelectWithValidation from '../components/inputs/BSelectWithValidation';
import BCheckboxGroupWithValidation from '../components/inputs/BCheckboxGroupWithValidation';
import BRadioGroupWithValidation from '../components/inputs/BRadioGroupWithValidation';

// Lodash (for Debounce) [Could do with importing just debounce but v5 removes per method packages]
// import _ from 'lodash';

export default {
	name: 'ApplicationForm',

	components: {
		MemberTypeCard,
		SelectedMemberNotification,
		TermsAndConditions,
		ValidationObserver,
		BInputWithValidation,
		BSelectWithValidation,
		BCheckboxGroupWithValidation,
		BRadioGroupWithValidation,
	},

	data() {
		return {
			availableMembershipTypes: [
				{
					type: 'Reduced',
					imageName: 'reduced',
					nhsMedical: 'Student',
					nonClinical: 'Scientist',
					nhs: 'Band 7 and below',
					price: '£40.00',
					memberCode: 'RM',
				},
				{
					type: 'Standard',
					imageName: 'standard',
					nhsMedical: 'FY, Core Trainees, STs and equivalent',
					nonClinical: 'Lecturer',
					nhs: 'Band 8a, 8b or 8c',
					price: '£105.00',
					memberCode: 'OM',
				},
				{
					type: 'Consultant',
					imageName: 'consultant',
					nhsMedical: 'Consultant',
					nonClinical: 'Senior Lecturer, Reader, Professor',
					nhs: 'Band 8d or above',
					price: '£150.00',
					memberCode: 'CM',
				},
			],

			titles: [],
			countries: [],
			counties: [],
			fieldsOfWork: [],
			affiliatedOrganizations: [],
			ediRoles: [],
			ediOrgans: [],
			ediAges: [],
			ediSexes: [],
			ediEthnicities: [],
			ediDisabilities: [],
			ediOrientations: [],
			ediResponsibilities: [],

			applicationData: {
				fields: {
					selectedMemberType: null,
					fieldsOfWork: [],
					primaryAddress: {},
					ediRole: null,
					ediRoleOther: '',
					ediOrgan: [],
					ediOrganOther: '',
					ediAge: null,
					ediSex: null,
					ediEthnicity: null,
					ediDisability: null,
					ediDisabilityOther: '',
					ediOrientation: null,
					ediResponsibility: [],
					ediApprove: null,
				},
			},

			termsAccepted: false,
			isSubmitting: false,

			// Stores the user's step 1 choice (Reduced/Standard/Consultant) so we can revert when they change affiliated org back to None
			lastStep1MemberType: null,

			formErrors: {
				errors: [],
				warnings: [],
			},
		};
	},

	computed: {
		apiBaseUrl() {
			return process.env.VUE_APP_API_BASE_URL || 'https://i20api.ksam.co.uk';
		},
		roleOtherRules() {
			return this.applicationData.fields.ediRole === 'OTH' ? 'required|max:200' : '';
		},
		organOtherRules() {
			return (this.applicationData.fields.ediOrgan || []).includes('OTH') ? 'required|max:200' : '';
		},
		disabilityOtherRules() {
			return this.applicationData.fields.ediDisability === 'OTH' ? 'required|max:200' : '';
		},
	},

	watch: {
		'applicationData.fields.selectedMemberType'() {
			this.updateMemberCode();
		},
		'applicationData.fields.affiliatedOrganization'(newVal) {
			const step1Types = ['Reduced', 'Standard', 'Consultant'];
			const current = this.applicationData.fields.selectedMemberType;

			if (newVal === 'BTPT') {
				if (step1Types.includes(current)) {
					this.lastStep1MemberType = current;
				}
				this.applicationData.fields.selectedMemberType = 'BTPT';
			} else if (newVal === 'HS') {
				if (step1Types.includes(current)) {
					this.lastStep1MemberType = current;
				}
				this.applicationData.fields.selectedMemberType = 'HERR';
			} else {
				// None or other org: revert to original step 1 choice if we had overridden to BTPT/HERR
				if ((current === 'BTPT' || current === 'HERR') && this.lastStep1MemberType != null) {
					this.applicationData.fields.selectedMemberType = this.lastStep1MemberType;
				}
			}
			this.updateMemberCode();
		},
		'applicationData.fields.ediRole'(newVal) {
			if (newVal !== 'OTH') {
				this.applicationData.fields.ediRoleOther = '';
			}
		},
		'applicationData.fields.ediOrgan'(newVal) {
			if (!(newVal || []).includes('OTH')) {
				this.applicationData.fields.ediOrganOther = '';
			}
		},
		'applicationData.fields.ediDisability'(newVal) {
			if (newVal !== 'OTH') {
				this.applicationData.fields.ediDisabilityOther = '';
			}
		},
	},

	created() {
		this.getApplicationFormData();
	},

	methods: {
		async handleSubmit() {
			console.log('🔍 Starting form validation...');
			
			try {
				const isValid = await this.$refs.observer.validate();
				console.log('✅ Validation result:', isValid);
				
				if (!isValid) {
					console.log('❌ Form validation failed');
					this.$buefy.toast.open({
						duration: 3000,
						message: 'Please fix all validation errors before submitting',
						type: 'is-warning',
					});
					return;
				}
				
				console.log('✅ Form validation passed, proceeding with submission');
				await this.submit();
			} catch (error) {
				console.error('💥 Error during validation:', error);
				this.$buefy.toast.open({
					duration: 3000,
					message: 'Validation error occurred',
					type: 'is-danger',
				});
			}
		},

		async submit() {
			console.log('🚀 Submit method called');
			console.log('📋 Form data:', this.applicationData);
			
			this.isSubmitting = true;
			
			// Enforce acceptance of Society Rules & Code of Conduct (cannot be bypassed via UI/devtools).
			// i20API should also require ediApprove === true and reject requests without it (prevents direct API calls).
			if (!this.termsAccepted || this.applicationData.fields.ediApprove !== true) {
				this.isSubmitting = false;
				this.$buefy.toast.open({
					duration: 5000,
					message: 'You must accept the Society Rules & Code of Conduct before submitting.',
					type: 'is-danger',
				});
				return;
			}
			
			try {
				//TODO: These Member checks need to be way more graceful
				console.log('🔍 Checking supporter 1:', this.applicationData.fields.firstSupporter);
				// Check Member Number 1
				const { data: supporterOneCheck } = await axios.get(
					`${this.apiBaseUrl}/bts/check-supporter/${this.applicationData.fields.firstSupporter}`
				);
				console.log('✅ Supporter 1 check result:', supporterOneCheck);

				console.log('🔍 Checking supporter 2:', this.applicationData.fields.secondSupporter);
				// Check Member Number 2
				const { data: supporterTwoCheck } = await axios.get(
					`${this.apiBaseUrl}/bts/check-supporter/${this.applicationData.fields.secondSupporter}`
				);
				console.log('✅ Supporter 2 check result:', supporterTwoCheck);

				// If neither member number is found
				if (supporterOneCheck.Count < 1 && supporterTwoCheck.Count < 1) {
					console.log('❌ Both supporters not found');
					this.$refs.observer.setErrors({
						'First Supporter Membership Number':
							'This Membership Number does not seem to exist.',
						'Second Supporter Membership Number':
							'This Membership Number does not seem to exist.',
					});

					this.$buefy.toast.open({
						duration: 3000,
						message: 'Supporter Number 1 & 2 does not seem to exist!',
						type: 'is-danger',
					});
					return;
				}

				// If only member number 1 isn't found
				if (supporterOneCheck.Count < 1) {
					console.log('❌ Supporter 1 not found');
					this.$refs.observer.setErrors({
						'First Supporter Membership Number':
							'This Membership Number does not seem to exist.',
					});

					this.$buefy.toast.open({
						duration: 3000,
						message: 'Supporter Number 1 does not seem to exist!',
						type: 'is-danger',
					});
					return;
				}

				// If only member number 2 isn't found
				if (supporterTwoCheck.Count < 1) {
					console.log('❌ Supporter 2 not found');
					this.$refs.observer.setErrors({
						'Second Supporter Membership Number':
							'This Membership Number does not seem to exist.',
					});

					this.$buefy.toast.open({
						duration: 3000,
						message: 'Supporter Number 2 does not seem to exist!',
						type: 'is-danger',
					});
					return;
				}

				console.log('✅ Both supporters validated successfully');

				// Ensure memberCode is up-to-date before submission
				this.updateMemberCode();

				// Sanitize postcode - trim whitespace and limit to 10 characters
				if (this.applicationData.fields.primaryAddress.postCode) {
					this.applicationData.fields.primaryAddress.postCode = 
						this.applicationData.fields.primaryAddress.postCode.trim().substring(0, 10);
				}

				// Map primaryAddress to both billing and mailing addresses
				this.applicationData.fields.billingAddress = this.applicationData.fields.primaryAddress;
				this.applicationData.fields.mailingAddress = this.applicationData.fields.primaryAddress;

				console.log('📤 Submitting form data to server...');
				console.log('📦 Final payload:', qs.stringify(this.applicationData));
				console.log('📋 Final memberCode:', this.applicationData.fields.memberCode);
				console.log('📮 Postcode being sent:', this.applicationData.fields.primaryAddress.postCode);

				const response = await axios.post(
					`${this.apiBaseUrl}/bts/application-form`,
					qs.stringify(this.applicationData)
				);

				console.log('📥 Server response:', response.data);

				if (response.data.Errors || response.data.Warnings) {
					console.log('⚠️ Server returned errors/warnings:', response.data);
					
					let errorMessages = [];
					
					if (response.data.Errors && response.data.Errors.$values) {
						response.data.Errors.$values.forEach((val) => {
							this.formErrors.errors.push(val);
							if (val.Message) {
								errorMessages.push(val.Message);
								console.error('❌ Server Error:', val.Message);
							}
						});
					}

					if (response.data.Warnings && response.data.Warnings.$values) {
						response.data.Warnings.$values.forEach((val) => {
							this.formErrors.warnings.push(val);
							if (val.Message) {
								console.warn('⚠️ Server Warning:', val.Message);
							}
						});
					}
					
					// Show user-friendly error message
					const displayMessage = errorMessages.length > 0 
						? errorMessages[0] 
						: 'Form submission completed with errors/warnings. Please check the form and try again.';
					
					this.$buefy.toast.open({
						duration: 8000,
						message: displayMessage,
						type: 'is-danger',
					});
				} else {
					console.log('🎉 Form submitted successfully!');
					this.$router.push(`/confirmation?name=${response.data.fullName}`);
				}
			} catch (error) {
				console.error('💥 Error during form submission:', error);
				console.error('Error details:', {
					message: error.message,
					response: error.response?.data,
					status: error.response?.status,
					config: error.config
				});
				
				this.$buefy.toast.open({
					duration: 5000,
					message: `Submission failed: ${error.message}`,
					type: 'is-danger',
				});
			} finally {
				this.isSubmitting = false;
			}
		},

		async getApplicationFormData() {
			console.log('📡 Loading application form data...');
			try {
				const response = await axios.get(
					`${this.apiBaseUrl}/bts/application-form`
				);
				const data = response.data;
				// API may return 200 with HTML error body (e.g. iisnode 500); treat as failure if not JSON with expected shape
				const isValidFormData = data && typeof data === 'object' && Array.isArray(data.titles);
				if (!isValidFormData) {
					console.error('💥 Form data response invalid (not JSON or wrong shape):', typeof data === 'string' ? data.substring(0, 200) : data);
					throw new Error('Server returned invalid form data. Please try again or contact support.');
				}
				console.log('✅ Form data loaded successfully:', data);
				
				this.titles = data.titles || [];
				this.countries = data.countries || [];
				this.counties = data.counties || [];
				this.fieldsOfWork = data.fieldsOfWork || [];
				this.affiliatedOrganizations = data.affiliatedOrganizations || [];
				this.ediRoles = data.ediRoles || [];
				this.ediOrgans = data.ediOrgans || [];
				this.ediAges = data.ediAges || [];
				this.ediSexes = data.ediSexes || [];
				this.ediEthnicities = data.ediEthnicities || [];
				this.ediDisabilities = data.ediDisabilities || [];
				this.ediOrientations = data.ediOrientations || [];
				this.ediResponsibilities = data.ediResponsibilities || [];
				
				console.log('📋 Form options populated:', {
					titles: this.titles.length,
					countries: this.countries.length,
					counties: this.counties.length,
					fieldsOfWork: this.fieldsOfWork.length,
					affiliatedOrganizations: this.affiliatedOrganizations.length,
					ediRoles: this.ediRoles.length,
					ediOrgans: this.ediOrgans.length,
					ediAges: this.ediAges.length,
					ediSexes: this.ediSexes.length,
					ediEthnicities: this.ediEthnicities.length,
					ediDisabilities: this.ediDisabilities.length,
					ediOrientations: this.ediOrientations.length,
					ediResponsibilities: this.ediResponsibilities.length,
				});
			} catch (error) {
				console.error('💥 Error loading form data:', error);
				const message = error.response && error.response.status === 500
					? 'Server error loading form. Please try again later.'
					: (error.message || 'Failed to load form data. Please refresh the page.');
				this.$buefy.toast.open({
					duration: 5000,
					message,
					type: 'is-danger',
				});
			}
		},

		acceptTerms() {
			this.termsAccepted = true;
			this.applicationData.fields.ediApprove = true;
			this.$buefy.toast.open({
				duration: 3000,
				message: 'Thank you for accepting the Society Rules & Code of Conduct',
				type: 'is-success',
			});
		},

		applyMemberCode(memberType) {
			console.log('🎯 Applying member code:', memberType);
			this.applicationData.fields.selectedMemberType = memberType.type;
			this.lastStep1MemberType = memberType.type; // remember for revert when affiliated org set back to None
			// Update memberCode will be triggered by watcher, but set base code first
			this.applicationData.fields.memberCode = memberType.memberCode;
			// Then update based on affiliate status
			this.updateMemberCode();
			console.log('✅ Member code set to:', this.applicationData.fields.memberCode);
		},

		updateMemberCode() {
			const selectedType = this.applicationData.fields.selectedMemberType;
			const affiliatedOrg = this.applicationData.fields.affiliatedOrganization;

			// BTPT Only / Herrick Only: membership type was set by affiliated org choice
			if (selectedType === 'BTPT' || selectedType === 'HERR') {
				this.applicationData.fields.memberCode = selectedType;
				return;
			}

			// Only apply affiliate logic to Reduced, Standard, or Consultant
			if (selectedType && affiliatedOrg && ['Reduced', 'Standard', 'Consultant'].includes(selectedType)) {
				// Map to affiliate member codes
				const affiliateCodeMap = {
					'Reduced': 'RA',
					'Standard': 'OA',
					'Consultant': 'CA'
				};

				const affiliateCode = affiliateCodeMap[selectedType];
				if (affiliateCode) {
					console.log(`🔄 Converting ${selectedType} to affiliate: ${affiliateCode}`);
					this.applicationData.fields.memberCode = affiliateCode;
					return;
				}
			}

			// Otherwise, use the base member code from the selected membership type
			if (selectedType) {
				const memberType = this.availableMembershipTypes.find(mt => mt.type === selectedType);
				if (memberType) {
					this.applicationData.fields.memberCode = memberType.memberCode;
					console.log('✅ Member code set to base:', this.applicationData.fields.memberCode);
				}
			}
		},
	},
};
</script>

<style>
.step-navigation {
	text-align: center;
}
</style>
